import React, { useState, useCallback } from 'react';
import { 
  Table, 
  Card, 
  Input, 
  Button, 
  Form, 
  Row, 
  Col, 
  message, 
  Tag, 
  Space,
  Typography,
  Tooltip,
  Pagination,
  Modal
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ClearOutlined
} from '@ant-design/icons';
import API from '../services/api';

const { Title, Text } = Typography;

const DynamicBlacklistManagement = () => {
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [blacklistData, setBlacklistData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [hasSearched, setHasSearched] = useState(false);
  const [statistics, setStatistics] = useState(null);
  
  // 搜索条件
  const [searchForm] = Form.useForm();
  const [currentHostIp, setCurrentHostIp] = useState('');

  // 获取动态黑名单数据
  const fetchBlacklistData = useCallback(async (hostIp, page = 1, limit = 20) => {
    if (!hostIp) {
      message.warning('请输入主机IP地址');
      return;
    }

    try {
      setLoading(true);
      const response = await API.get('/api/admin/blacklist', {
        params: {
          host_ip: hostIp,
          keywords: '',
          page,
          limit
        }
      });

      if (response.data && response.data.code === 0) {
        const data = response.data.data;
        setBlacklistData(data.list || []);
        setTotalRecords(data.count || data.pagination?.total || 0);
        setCurrentPage(data.pagination?.page || page);
        setStatistics(data.statistics || null);
        setHasSearched(true);
        
        if (data.list && data.list.length === 0) {
          message.info('该IP没有找到动态黑名单记录');
        } else {
          message.success(`成功获取 ${data.count || data.pagination?.total || 0} 条黑名单记录`);
        }
      } else {
        message.error(response.data.msg || '获取动态黑名单失败');
        setBlacklistData([]);
        setTotalRecords(0);
        setHasSearched(true);
      }
    } catch (error) {
      console.error('获取动态黑名单失败:', error);
      let errorMsg = '获取动态黑名单失败';
      
      if (error.response && error.response.data) {
        errorMsg = error.response.data.msg || error.response.data.message || errorMsg;
      }
      
      message.error(errorMsg);
      setBlacklistData([]);
      setTotalRecords(0);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // 处理搜索
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    const { hostIp } = values;
    
    if (!hostIp || hostIp.trim() === '') {
      message.warning('请输入主机IP地址');
      return;
    }

    setCurrentHostIp(hostIp.trim());
    setCurrentPage(1);
    fetchBlacklistData(hostIp.trim(), 1, pageSize);
  };

  // 处理刷新
  const handleRefresh = () => {
    if (!currentHostIp) {
      message.warning('请先进行搜索');
      return;
    }
    fetchBlacklistData(currentHostIp, currentPage, pageSize);
  };

  // 处理分页变化
  const handlePageChange = (page, size) => {
    if (!currentHostIp) {
      message.warning('请先进行搜索');
      return;
    }
    setCurrentPage(page);
    setPageSize(size);
    fetchBlacklistData(currentHostIp, page, size);
  };

  // 清空所有黑名单
  const handleClearAllBlacklist = () => {
    if (!currentHostIp) {
      message.warning('请先进行搜索');
      return;
    }

    Modal.confirm({
      title: '确认清空所有黑名单',
      content: `确定要清空主机 ${currentHostIp} 下的所有动态黑名单记录吗？此操作不可恢复！`,
      okText: '确定清空',
      cancelText: '取消',
      okType: 'danger',
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          setLoading(true);
          const response = await API.delete('/api/admin/blacklist', {
            params: { host_ip: currentHostIp }
          });

          if (response.data && response.data.code === 0) {
            message.success('清空动态黑名单成功');
            // 重新获取数据
            fetchBlacklistData(currentHostIp, 1, pageSize);
          } else {
            message.error(response.data.msg || '清空动态黑名单失败');
          }
        } catch (error) {
          console.error('清空动态黑名单失败:', error);
          let errorMsg = '清空动态黑名单失败';
          
          if (error.response && error.response.data) {
            errorMsg = error.response.data.msg || error.response.data.message || errorMsg;
          }
          
          message.error(errorMsg);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // 格式化IP地址
  const formatIP = (ip) => {
    if (!ip) return '-';
    return (
      <Tag color="blue" style={{ fontFamily: 'monospace' }}>
        {ip}
      </Tag>
    );
  };



  // 表格列定义
  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1
    },
    {
      title: '被封禁IP地址',
      dataIndex: 'ban_ip',
      key: 'ban_ip',
      width: 150,
      render: formatIP
    },
    {
      title: '规则名称',
      dataIndex: 'rule_name',
      key: 'rule_name',
      width: 120,
      render: (ruleName) => (
        ruleName ? (
          <Tag color="blue">{ruleName}</Tag>
        ) : (
          <Tag color="default">系统规则</Tag>
        )
      )
    },
    {
      title: '封禁时长',
      dataIndex: 'block_time',
      key: 'block_time',
      width: 120,
      render: (blockTime, record) => {
        const hours = record.block_time_hours || 0;
        const minutes = record.block_time_minutes || 0;
        
        if (hours > 0) {
          return (
            <Text strong style={{ color: '#ff4d4f' }}>
              {hours}小时{minutes > 0 ? `${minutes}分钟` : ''}
            </Text>
          );
        } else if (minutes > 0) {
          return (
            <Text strong style={{ color: '#1890ff' }}>
              {minutes}分钟
            </Text>
          );
        } else {
          return (
            <Text type="secondary">
              {blockTime || 0}秒
            </Text>
          );
        }
      }
    },
    {
      title: '创建时间',
      dataIndex: 'create_time_formatted',
      key: 'create_time_formatted',
      width: 180,
      render: (time) => time || '-'
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: () => (
        <Tag color="red" icon={<ExclamationCircleOutlined />}>
          已封禁
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Tooltip title="单个移除功能暂未实现">
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled
          />
        </Tooltip>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>动态黑名单管理</Title>
        <Text type="secondary">
          查看和管理系统动态黑名单，监控恶意IP地址和攻击行为
        </Text>
      </div>

      {/* 搜索条件卡片 */}
      <Card title="查询条件" style={{ marginBottom: '16px' }}>
        <Form 
          form={searchForm}
          layout="vertical"
          onFinish={handleSearch}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="hostIp"
                label="主机IP地址"
                rules={[
                  { required: true, message: '请输入主机IP地址' },
                  {
                    pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                    message: '请输入正确的IP地址格式'
                  }
                ]}
              >
                <Input 
                  placeholder="请输入要查询的主机IP地址"
                  suffix={
                    <Tooltip title="输入要查询黑名单的主机IP地址">
                      <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </Tooltip>
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label=" " colon={false}>
                <Space>
                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />} 
                    htmlType="submit"
                    loading={loading}
                  >
                    查询
                  </Button>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={handleRefresh}
                    loading={loading}
                    disabled={!hasSearched}
                  >
                    刷新
                  </Button>
                  <Button 
                    danger
                    icon={<ClearOutlined />} 
                    onClick={handleClearAllBlacklist}
                    loading={loading}
                    disabled={!hasSearched || totalRecords === 0}
                  >
                    清空所有黑名单
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 统计信息 */}
      {hasSearched && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Text>查询主机: </Text>
                <Tag color="blue">{currentHostIp}</Tag>
              </Space>
            </Col>
            <Col>
              <Space>
                <Text strong>
                  共找到 {totalRecords} 条黑名单记录
                </Text>
                {statistics && (
                  <Tag color="volcano">
                    被封禁IP: {statistics.total_blocked_ips}
                  </Tag>
                )}
              </Space>
            </Col>
          </Row>
        </Card>
      )}



      {/* 数据表格 */}
      <Card title="黑名单记录" size="small">
        <Table
          columns={columns}
          dataSource={blacklistData}
          rowKey={(record, index) => record.id || `${record.ban_ip}_${index}`}
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 1200 }}
          locale={{
            emptyText: hasSearched ? '暂无黑名单记录' : '请输入查询条件进行搜索'
          }}
        />
        
        {/* 自定义分页 */}
        {hasSearched && totalRecords > 0 && (
          <div style={{ marginTop: '16px', textAlign: 'right' }}>
            <Pagination
              current={currentPage}
              total={totalRecords}
              pageSize={pageSize}
              showTotal={(total, range) => 
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
              }
              showSizeChanger
              showQuickJumper
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
            />
          </div>
        )}
      </Card>


    </div>
  );
};

export default DynamicBlacklistManagement; 